import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  User,
  Search,
  FileText,
  Send,
  ArrowRight,
  ArrowDown,
} from "lucide-react";

/* ================= DATA ================= */

const steps = [
  {
    id: "01",
    title: "Career Guidance & Student Profile Review",
    desc: "No charges. Clear guidance from the start.",
    icon: User,
  },
  {
    id: "02",
    title: "University Applications & Exam Support",
    desc: "No running behind colleges or paperwork stress.",
    icon: Search,
  },
  {
    id: "03",
    title: "Admission Offers, Scholarships & Visa Assistance",
    desc: "Transparency at every stage. No hidden charges.",
    icon: FileText,
  },
  {
    id: "04",
    title: "Pre-Departure Support & Settling Abroad",
    desc: "Parents get peace of mind even after departure.",
    icon: Send,
  },
];

/* ================= ANIMATION VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.35, // Card → Arrow → Card → Arrow
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const arrowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function WorkflowSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      className="py-24 bg-[#BFD8FF]"
    >
      <div className="max-w-7xl mx-auto px-6 text-center text-[#000000]">

        {/* Heading */}
        <p className="text-2xl font-bold uppercase tracking-widest mb-4">
          Our Workflow
        </p>

        <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight">
          See How We <span className="text-[#27378E]">Operate</span> and{" "}
          <span className="text-[#27378E]">Manage</span>
          <br /> Efficiently.
        </h2>

        <div className="flex justify-center my-12">
          <span className="w-40 h-px bg-[#27378E]" />
        </div>

        {/* STAGGER CONTAINER */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="flex justify-center"
        >
          <div className="flex flex-col lg:flex-row items-center">

            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div key={index} className="flex flex-col lg:flex-row items-center">

                  {/* CARD */}
                 <motion.div
  variants={cardVariants}
  className="relative w-[260px] h-[280px]"
>

  {/* OUTER BORDER RING — NO OVERFLOW HIDDEN */}
  <div className="
    absolute -inset-[3px]
    rounded-2xl
    bg-[conic-gradient(#27378E,#60A5FA,#27378E)]
    opacity-0
    transition duration-300
    group-hover:opacity-100
    group-hover:animate-spin-slow
    blur-[0.3px]
  "></div>

  {/* MAIN CARD */}
  <div className="
  group relative
  w-full h-full
  bg-white
  rounded-2xl
  shadow-xl
  transition-all duration-500
  hover:bg-[#27378E]
  hover:shadow-[0_20px_50px_-15px_rgba(39,55,142,0.45)]
">


    {/* CONTENT */}
    <div className="relative z-20 px-6 pt-16 pb-8 h-full flex flex-col items-center">

      {/* NUMBER */}
      <div className="
        absolute -top-6 left-1/2 -translate-x-1/2
        bg-[#27378E] text-white font-bold
        text-lg px-6 py-2 rounded-xl shadow-md
        transition duration-300 group-hover:bg-[#ffffff]  group-hover:text-[#27378E]
      ">
        {step.id}
      </div>

      {/* ICON */}
      <div className="mb-4 mt-2">
        <div className="
          w-12 h-12 rounded-xl 
          bg-[#2563eb]/10 
          flex items-center justify-center
          transition duration-300 group-hover:bg-[#ffffff]
        ">
          <Icon size={22} className="text-[#2563eb] group-hover:text-[#27378E]" />
        </div>
      </div>

      {/* TITLE */}
      <h3 className="font-serif text-[13.5px] font-semibold text-center mb-2 group-hover:text-white cursor-default">
        {step.title}
      </h3>

      {/* DESC */}
      <p className="text-[12.5px] text-slate-600 text-center font-bold mt-auto  group-hover:text-white cursor-default ">
        {step.desc}
      </p>

    </div>
  </div>
</motion.div>



                  {/* ARROWS */}
                  {/* ARROWS */}
{index !== steps.length - 1 && (
  <>
    {/* Desktop Arrow */}
    <motion.div
      variants={arrowVariants}
      className="hidden lg:flex mx-6"
    >
      <div
        className="
          w-12 h-12
          rounded-full
          bg-white
          flex items-center justify-center
          shadow-lg
        "
      >
        <ArrowRight size={22} className="text-[#27378E]" />
      </div>
    </motion.div>

    {/* Mobile Arrow */}
    <motion.div
      variants={arrowVariants}
      className="flex lg:hidden my-6"
    >
      <div
        className="
          w-12 h-12
          rounded-full
          bg-white
          flex items-center justify-center
          shadow-lg
        "
      >
        <ArrowDown size={22} className="text-[#27378E]" />
      </div>
    </motion.div>
  </>
)}

                </div>
              );
            })}

          </div>
        </motion.div>

      </div>
    </section>
  );
}
